import { useMemo } from 'react';
import { AddressFields as DesignAddressFields, type AddressFieldKey, type AddressFieldSpec, type AddressValue } from '@v0y4ge/jrni-ui';
import { addressFormatFor, resolveRegion, resolveCountry, ADDRESS_FORMATS, SUPPORTED_ADDRESS_COUNTRIES } from '@v0y4ge/api-contracts';
export type AddressKey = AddressFieldKey;
export type { AddressValue } from '@v0y4ge/jrni-ui';
export interface AddressFieldsProps {
  value: AddressValue;
  onChange: (patch: Partial<AddressValue>) => void;
  onBlur?: (field: AddressKey) => void;
  errors?: Partial<Record<AddressKey, string>>;
  readOnly?: boolean;
  idPrefix?: string;
  size?: 'sm' | 'md' | 'lg';
}
const autocomplete: Record<AddressKey, string> = {
  country: 'country', address: 'address-line1', addressLine2: 'address-line2',
  city: 'address-level2', region: 'address-level1', postalCode: 'postal-code',
};
/** Bind the shared design control to canonical country formats and region options. */
export function AddressFields({ value, onChange, ...props }: AddressFieldsProps) {
  const format = addressFormatFor(value.country);
  const countries = useMemo(() => SUPPORTED_ADDRESS_COUNTRIES.map(code => ({
    value: code, label: `${ADDRESS_FORMATS[code].name} (${code})`,
  })).sort((a, b) => a.label.localeCompare(b.label)), []);
  const country = resolveCountry(value.country) || value.country;
  const region = resolveRegion(value.country, value.region) || value.region;
  const countryOptions = useMemo(() => country && !countries.some(option => option.value === country)
    ? [...countries, { value: country, label: value.country }] : countries, [countries, country, value.country]);
  const fieldRows = useMemo(() => [...new Set(format.fields.map(field => field.row))].sort((a, b) => a - b)
    .map(row => format.fields.filter(field => field.row === row).map(field => field.key as AddressKey)), [format]);
  const fieldSpecs = useMemo(() => {
    const specs: Partial<Record<AddressKey, AddressFieldSpec>> = {
      country: { label: 'Country', required: true, placeholder: 'Choose a country…' },
    };
    for (const field of format.fields) {
      const key = field.key as AddressKey;
      const options = field.options?.map(option => ({ value: option, label: option }));
      if (options && key === 'region' && region && !options.some(option => option.value === region)) {
        options.push({ value: region, label: value.region });
      }
      specs[key] = {
        label: field.label, required: field.required, placeholder: field.placeholder ?? '',
        autoComplete: autocomplete[key], inputMode: 'text', options, span: field.span ?? 1,
      };
    }
    return specs;
  }, [format, region, value.region]);
  const displayValue = useMemo(() => ({ ...value, country, region }), [value, country, region]);
  return <DesignAddressFields {...props} value={displayValue} countryOptions={countryOptions}
    fieldSpecs={fieldSpecs} fieldRows={fieldRows} countryHint={format.note}
    onChange={(field, next) => onChange({ [field]: next })} />;
}
